// Round-end and game-end modals. Both use the same card structure:
//   • team-colored header with "Red wins Round 4!" / "YOU WON!" / "YOU LOST!"
//   • three crown slots to the right of the header title
//   • one row per member of the winning team (or local team on game-over)
//   • game-end variant adds a blue "Back to Lobby" button below
//
// Round-end dismisses automatically after `AUTO_DISMISS_MS`; game-end waits
// for the player to click Back to Lobby and invokes the caller's onBackToLobby
// handler.

import type { MatchState, TeamMember } from '../sim/MatchState';
import { TEAM_COLORS, ROUNDS_TO_WIN } from '../sim/MatchState';
import type { TeamId } from '../sim/SimState';
import { renderHeadIcon } from './HeadIcon';
import { MenuNav, type MenuItem } from './MenuNav';
import { t } from '../i18n';

const AUTO_DISMISS_MS = 4000;
const CROWN_FILL_DELAY_MS = 300;
const CROWN_SRC = '/ui/game-crown.webp';
const ROUND_WIN_SFX = '/sounds/round-crown.ogg';
/** Mirrors PLAY_AGAIN_WINDOW_MS on the server. Used as the initial
 *  countdown display before the caller's interval ticks. */
const PLAY_AGAIN_WINDOW_SECONDS = 15;

export interface RoundEndOptions {
  /** Called after the round-end modal auto-dismisses. Implementation is
   *  expected to reset the world for the next round. */
  onContinue: () => void;
}

export interface GameEndOptions {
  onBackToLobby: () => void;
  /** Optional: if provided, a "Play Again" button is rendered alongside
   *  Back to Lobby. Used for online matches — the caller wires the click
   *  to send `playAgain { yes: true }` to the GameRoom and to listen for
   *  `playAgain:reservation` to reconnect into a regroup PartyRoom. The
   *  caller is responsible for closing the modal once the regroup
   *  reservation arrives (or for marking the button as disabled after
   *  click). */
  onPlayAgain?: () => void;
  /** When true, the caller wants the modal to display a 15 s countdown
   *  beside the Play Again button. Driven by the caller via setSeconds()
   *  on the returned handle. */
  showPlayAgainTimer?: boolean;
}

export interface GameEndModalHandle {
  dismiss(): void;
  /** Update the play-again countdown badge. Caller is the source of truth
   *  for the seconds remaining. */
  setPlayAgainSeconds(seconds: number): void;
  /** Disable the Play Again button (after a click — the caller doesn't
   *  let users toggle their consent). */
  setPlayAgainPressed(): void;
  /** Indicate the play-again window has closed (regrouped or not). Hides
   *  the button + timer. */
  setPlayAgainClosed(regrouped: boolean): void;
}

/**
 * Show the round-end modal. Returns an object with a `dismiss()` method for
 * manual cleanup — normally unused because the modal auto-dismisses.
 */
export function showRoundEndModal(match: MatchState, opts: RoundEndOptions): { dismiss: () => void } {
  const winner = match.lastRoundWinner;
  const hostRoot = ensureRoot();
  releasePointerLock();
  const { root, crowns } = renderModal(match, {
    mode: 'round',
    winner,
    winningTeamForHeader: winner ?? match.localTeam() // draw → local team color
  });
  hostRoot.appendChild(root);

  // Delay the crown fill animation by 300ms so the modal settles first.
  // Play the SFX at the same moment.
  let dismissed = false;
  const crownFillTimer = window.setTimeout(() => {
    if (dismissed) return;
    const idx = winner === null
      ? -1
      : match.crownsBeforeLatestWin[winner]; // the slot that just flipped on
    if (winner !== null && idx >= 0 && crowns[idx]) {
      crowns[idx]!.classList.remove('off');
      crowns[idx]!.classList.add('pop');
    }
    playSfx(ROUND_WIN_SFX);
  }, CROWN_FILL_DELAY_MS);

  const dismissTimer = window.setTimeout(() => {
    dismiss();
    opts.onContinue();
  }, AUTO_DISMISS_MS);

  function dismiss(): void {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(crownFillTimer);
    window.clearTimeout(dismissTimer);
    root.classList.add('closing');
    setTimeout(() => root.remove(), 280);
  }

  return { dismiss };
}

/**
 * Show the game-end modal. Auto-dismisses only on user click of "Back to
 * Lobby" — that handler is what triggers the swipe-out / home-return flow.
 */
export function showGameEndModal(match: MatchState, opts: GameEndOptions): GameEndModalHandle {
  const winner = match.lastRoundWinner;
  const hostRoot = ensureRoot();
  releasePointerLock();
  const { root, crowns } = renderModal(match, {
    mode: 'game',
    winner,
    winningTeamForHeader: match.localTeam(),
    showPlayAgainButton: !!opts.onPlayAgain
  });
  hostRoot.appendChild(root);

  // Animate the final crown fill + sfx just like round-end, then leave the
  // button interactive indefinitely.
  let dismissed = false;
  window.setTimeout(() => {
    if (dismissed) return;
    if (winner !== null) {
      const idx = match.crownsBeforeLatestWin[winner];
      if (idx >= 0 && crowns[idx]) {
        crowns[idx]!.classList.remove('off');
        crowns[idx]!.classList.add('pop');
      }
    }
    playSfx(ROUND_WIN_SFX);
  }, CROWN_FILL_DELAY_MS);

  // MenuNav on the back-to-lobby button (and play-again, when present)
  // so keyboard / gamepad / mouse all route through one activate path
  // and the UI-click sound plays consistently.
  const backBtn = root.querySelector('.back-btn') as HTMLButtonElement | null;
  const playAgainBtn = root.querySelector('.play-again-btn') as HTMLButtonElement | null;
  const playAgainTimerEl = root.querySelector('.play-again-timer') as HTMLElement | null;
  let nav: MenuNav | null = null;
  if (backBtn || playAgainBtn) {
    const items: MenuItem[] = [];
    if (playAgainBtn) {
      items.push({
        el: playAgainBtn,
        row: 0, col: 0,
        onActivate: () => {
          if (playAgainBtn.classList.contains('pressed')) return;
          opts.onPlayAgain?.();
          handle.setPlayAgainPressed();
        }
      });
    }
    if (backBtn) {
      items.push({
        el: backBtn,
        row: 0, col: items.length,
        onActivate: () => {
          dismiss();
          opts.onBackToLobby();
        }
      });
    }
    nav = new MenuNav({ items, initialIndex: 0 });
    nav.mount();
  }

  function dismiss(): void {
    if (dismissed) return;
    dismissed = true;
    nav?.dispose();
    nav = null;
    root.classList.add('closing');
    setTimeout(() => root.remove(), 280);
  }

  const handle: GameEndModalHandle = {
    dismiss,
    setPlayAgainSeconds(seconds: number): void {
      if (!playAgainTimerEl) return;
      // Clamp aggressively. NaN/Infinity/huge floats (e.g. a stale absolute
      // timestamp accidentally passed in instead of a remaining-seconds
      // value) would otherwise render in scientific notation.
      const safe = Number.isFinite(seconds)
        ? Math.max(0, Math.min(99, Math.floor(seconds)))
        : 0;
      playAgainTimerEl.textContent = `${safe}s`;
    },
    setPlayAgainPressed(): void {
      if (!playAgainBtn) return;
      playAgainBtn.classList.add('pressed');
      playAgainBtn.disabled = true;
      const span = playAgainBtn.querySelector('.play-again-label') as HTMLElement | null;
      if (span) span.textContent = t('roundEnd.youInPlayAgain');
    },
    setPlayAgainClosed(regrouped: boolean): void {
      if (playAgainBtn) playAgainBtn.style.display = 'none';
      if (playAgainTimerEl) playAgainTimerEl.style.display = 'none';
      if (!regrouped) {
        // No regroup happened — the back-to-lobby flow runs in due
        // course (the GameRoom disposes shortly after).
      }
    }
  };
  // Seed the timer with the configured window length so it never appears
  // empty (or worse, with stale text from a previous mount) before the
  // caller's interval ticks.
  if (opts.showPlayAgainTimer && playAgainBtn) {
    handle.setPlayAgainSeconds(PLAY_AGAIN_WINDOW_SECONDS);
  }
  return handle;
}

// ---------------------------------------------------------------------------
// Implementation details

type ModalMode = 'round' | 'game';

interface RenderOpts {
  mode: ModalMode;
  winner: TeamId | null; // null = draw
  winningTeamForHeader: TeamId;
  /** Game mode only: show the Play Again button + 15 s timer beside
   *  Back to Lobby. */
  showPlayAgainButton?: boolean;
}

function renderModal(match: MatchState, opts: RenderOpts): {
  root: HTMLElement;
  crowns: HTMLImageElement[];
} {
  const team = opts.winningTeamForHeader;
  const tokens = TEAM_COLORS[team];
  const root = document.createElement('div');
  root.className = 'match-modal';

  // Which members to list in the body — round: winning team (or local team
  // on draw); game: local team. The screenshots show the player's own team.
  const bodyTeam: TeamId = opts.mode === 'game' ? match.localTeam()
    : (opts.winner ?? match.localTeam());
  const members = match.membersOfTeam(bodyTeam);

  // Header text.
  const headerText = opts.mode === 'round'
    ? (opts.winner === null
        ? t('roundEnd.draw', { round: match.round })
        : (opts.winner === 'red'
            ? t('roundEnd.redWins', { round: match.round })
            : t('roundEnd.blueWins', { round: match.round })))
    : (opts.winner === match.localTeam() ? t('roundEnd.youWon') : t('roundEnd.youLost'));

  root.innerHTML = `
    <div class="match-modal-backdrop"></div>
    <div class="match-modal-card">
      <div class="match-modal-header" style="background:${tokens.primary};">
        <div class="header-title">${escapeHtml(headerText)}</div>
        <div class="header-crowns">
          ${[0, 1, 2].map((i) => {
            const won = opts.winner !== null
              ? (i < match.crowns[opts.winner])
              : (i < match.crowns[team]);
            // Animate: crowns won BEFORE this round start in their final
            // state; the one that just popped starts off and fades on after
            // a short delay (handled in show*Modal).
            const startedWon = i < (opts.winner !== null
              ? match.crownsBeforeLatestWin[opts.winner]
              : match.crowns[team]);
            return `<img class="crown ${startedWon ? '' : 'off'}" src="${CROWN_SRC}" alt="" draggable="false" data-idx="${i}" data-won="${won ? '1' : '0'}"/>`;
          }).join('')}
        </div>
      </div>
      <div class="match-modal-body">
        ${members.map((m) => renderMemberRow(m)).join('')}
        ${opts.mode === 'game' ? `
          <div class="game-actions">
            ${opts.showPlayAgainButton
              ? `<button class="play-again-btn" type="button"><span class="play-again-label">${t('roundEnd.playAgain')}</span> <span class="play-again-timer"></span></button>`
              : ''}
            <button class="back-btn" type="button">${t('roundEnd.backToLobby')}</button>
          </div>
        ` : ''}
      </div>
    </div>
    ${STYLE_BLOCK}
  `;
  // Kick off head-icon renders and swap them in as they arrive.
  for (const m of members) {
    void fillHeadIcon(root, m);
  }
  const crowns = Array.from(root.querySelectorAll<HTMLImageElement>('.header-crowns .crown'));
  return { root, crowns };
}

function renderMemberRow(m: TeamMember): string {
  return `
    <div class="member-row">
      <span class="row-head" id="row-head-${m.id}" style="background:${m.color};"></span>
      <span class="row-name">${escapeHtml(m.name)}</span>
      ${m.isLocal ? `<span class="you-chip">${t('roundEnd.you')}</span>` : ''}
    </div>
  `;
}

async function fillHeadIcon(root: HTMLElement, m: TeamMember): Promise<void> {
  try {
    const canvas = await renderHeadIcon({ color: m.color, hatId: m.hatId, size: 96 });
    const url = canvas.toDataURL('image/png');
    const el = root.querySelector(`#row-head-${m.id}`) as HTMLElement | null;
    if (!el) return;
    el.style.backgroundImage = `url(${url})`;
    el.style.backgroundColor = 'transparent';
    el.style.backgroundSize = 'cover';
    el.style.backgroundPosition = 'center 22%';
  } catch { /* icon stays as a flat color fallback */ }
}

/** Release pointer lock so the user's cursor is free to interact with the
 *  modal (click "Back to Lobby", scroll, etc). Input.ts watches
 *  pointerlockchange and flips itself into the "released" state on its own,
 *  which also suspends input polling — exactly what we want during modals. */
function releasePointerLock(): void {
  if (document.pointerLockElement) {
    try { document.exitPointerLock?.(); } catch { /* browser may throw — ignore */ }
  }
}

function ensureRoot(): HTMLElement {
  let root = document.getElementById('match-modals');
  if (!root) {
    root = document.createElement('div');
    root.id = 'match-modals';
    // pointer-events:none on the container so an empty #match-modals (after
    // a round-end auto-dismisses) doesn't keep intercepting clicks over the
    // whole viewport — that was blocking the canvas (can't re-acquire input)
    // and the debug panel (can't click fields). Modal cards inside still
    // receive clicks via their default pointer-events:auto.
    root.style.cssText = `position:fixed; inset:0; z-index:150; pointer-events:none;`;
    document.body.appendChild(root);
  }
  return root;
}

let sfxCache: HTMLAudioElement | null = null;
function playSfx(src: string): void {
  try {
    // Fresh clone each time — browser doesn't reset playback on the source
    // object reliably if a previous play is still in flight.
    const base = (sfxCache ??= new Audio(src));
    const a = base.cloneNode(true) as HTMLAudioElement;
    a.volume = 0.9;
    void a.play().catch(() => {/* autoplay block — ignore */});
  } catch { /* best-effort */ }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

void ROUNDS_TO_WIN; // may be referenced in future logic / exported for tests

const STYLE_BLOCK = `
<style>
  .match-modal { position: fixed; inset: 0; z-index: 1; pointer-events: auto; font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif; }
  .match-modal.closing { animation: matchModalOut 280ms cubic-bezier(0.3, 0, 0.2, 1) forwards; }
  @keyframes matchModalOut { to { opacity: 0; transform: scale(0.96); } }
  .match-modal-backdrop {
    position: absolute; inset: 0;
    background: rgba(0, 0, 0, 0.6);
    /* No backdrop-filter: blur — at full-viewport it nukes GPU perf
       (FPS dropped to single digits while the modal was up). The plain
       darken reads fine on its own. */
    animation: matchFadeIn 200ms ease-out;
  }
  @keyframes matchFadeIn { from { opacity: 0; } to { opacity: 1; } }

  .match-modal-card {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    min-width: 400px; max-width: 90vw;
    background: #1E232C;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 16px 48px rgba(0,0,0,0.5);
    animation: matchCardIn 300ms cubic-bezier(0.2, 0.9, 0.2, 1);
  }
  @keyframes matchCardIn {
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
    to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }

  .match-modal-header {
    display: flex; align-items: center; gap: 14px;
    padding: 10px 16px;
    color: #FFFFFF;
    -webkit-text-stroke: 4px rgba(0,0,0,0.22);
    paint-order: stroke fill;
  }
  .match-modal-header .header-title {
    flex: 1; font-size: 26px; line-height: 1; padding-right: 8px;
  }
  .match-modal-header .header-crowns {
    display: flex; gap: 3px;
  }
  .match-modal-header .crown {
    width: 28px; height: 28px;
    object-fit: contain;
    filter: none;
    transition: filter 400ms cubic-bezier(0.2, 0.9, 0.2, 1),
                transform 400ms cubic-bezier(0.2, 0.9, 0.2, 1);
  }
  .match-modal-header .crown.off {
    filter: grayscale(1) brightness(0.45);
  }
  .match-modal-header .crown.pop {
    animation: crownPop 520ms cubic-bezier(0.2, 0.9, 0.2, 1);
  }
  @keyframes crownPop {
    0%   { transform: scale(0.65); filter: grayscale(1) brightness(0.45); }
    55%  { transform: scale(1.35); filter: none; }
    100% { transform: scale(1);    filter: none; }
  }

  .match-modal-body {
    padding: 12px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .member-row {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px;
    background: #FFFFFF;
    color: #1E232C;
    border-radius: 12px;
  }
  .member-row .row-head {
    display: inline-block;
    width: 28px; height: 28px;
    border-radius: 50%;
    border: 2px solid rgba(0,0,0,0.25);
    flex-shrink: 0;
    background-size: cover;
    background-position: center 22%;
  }
  .member-row .row-name {
    flex: 1;
    font-size: 22px;
    line-height: 1;
  }
  .member-row .you-chip {
    font-size: 16px;
    color: #FFFFFF;
    background: #66707E;
    padding: 4px 10px;
    border-radius: 8px;
    letter-spacing: 0.04em;
  }

  .game-actions {
    display: flex; gap: 10px;
    margin-top: 4px;
  }
  .game-actions .back-btn { flex: 1; }
  .game-actions .play-again-btn { flex: 1; }

  .back-btn {
    border: 0; cursor: pointer;
    font-family: 'Chewy', sans-serif;
    font-size: 26px;
    color: #FFFFFF;
    -webkit-text-stroke: 3px #356B8C;
    paint-order: stroke fill;
    padding: 12px 0;
    background: linear-gradient(180deg, #7DD2F5 0%, #3DA6FF 100%);
    box-shadow: 0 4px 0 #256AA0, 0 8px 18px rgba(0,0,0,0.3);
    border-radius: 14px;
    transition: transform 120ms ease, box-shadow 120ms ease;
  }
  .back-btn:hover,
  .back-btn.focused { filter: brightness(1.04); }
  .back-btn.focused { outline: 4px solid #7DD2F5; outline-offset: 3px; }
  .back-btn:active {
    transform: translateY(3px);
    box-shadow: 0 1px 0 #256AA0, 0 3px 10px rgba(0,0,0,0.2);
  }

  .play-again-btn {
    border: 0; cursor: pointer;
    font-family: 'Chewy', sans-serif;
    font-size: 22px;
    color: #FFFFFF;
    -webkit-text-stroke: 3px #B14400;
    paint-order: stroke fill;
    padding: 12px 0;
    background: linear-gradient(180deg, #FFAA66 0%, #FF8A1E 100%);
    box-shadow: 0 4px 0 #B14400, 0 8px 18px rgba(0,0,0,0.3);
    border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    gap: 8px;
    transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
  }
  .play-again-btn:hover,
  .play-again-btn.focused { filter: brightness(1.04); outline: 4px solid #FFAA66; outline-offset: 3px; }
  .play-again-btn:active { transform: translateY(3px); box-shadow: 0 1px 0 #B14400; }
  .play-again-btn[disabled],
  .play-again-btn.pressed {
    cursor: default;
    background: linear-gradient(180deg, #66707E 0%, #4A535E 100%);
    box-shadow: 0 4px 0 #2C333B;
    filter: none;
  }
  .play-again-btn .play-again-timer {
    background: rgba(0,0,0,0.25);
    border-radius: 8px;
    padding: 2px 8px;
    font-size: 18px;
    -webkit-text-stroke: 2px rgba(0,0,0,0.35);
  }
</style>
`;
